<!--
  CartonDoneOrdering — the slot of an ACTIVE carton in the two-lane cycle
  (done-ordering pack, prompt 3; brief 2026-10-17-done-ordering-review).

  Regular lane: she taps «خلصت طلباتي» (nothing is cancelled, collection goes
  on) → the team finishes her lines and prepares the carton → the team SENDS her
  the review → she accepts (today's ask-to-ship, with its out-of-stock approval —
  that stays the PAGE's confirm, reached through the `ship` event), writes a
  carton-level complaint, or opens ordering again.
  Fast lane: today's «طلب الشحن» block, passed in as the default slot and left
  exactly as the page draws it. This component only decides WHEN it shows and
  tells it (slot prop `fastLane`) to step down to an outline.

  Which block shows is `cartonReviewState`'s answer, from the carton and its lines
  (see that file). The server owns every rule and refuses with a 409 carrying her
  sentence; this component only declines to OFFER what the server would refuse. On
  a 409 the sentence is printed verbatim and the carton is re-read.

  The word «close» is taken here («مغلقة» tab, «تمت التسوية» status) — the concept
  is "done ordering".
-->
<template>
  <div class="carton-done">
    <!-- LOADING — a closed carton whose lines are still coming in: claim nothing. -->
    <f7-block v-if="state === CartonReview.LOADING" class="carton-done__loading">
      <f7-preloader size="22"></f7-preloader>
    </f7-block>

    <!-- OPEN — «خلصت طلباتي» above today's ship-gate block (the fast lane). -->
    <f7-block v-if="state === CartonReview.OPEN">
      <f7-button
        fill
        large
        class="carton-done__cta"
        :disabled="isBusy"
        @click="confirmDone"
      >
        <f7-preloader
          v-if="isMarkingDoneOrdering"
          size="20"
          class="margin-right-half"
        ></f7-preloader>
        <f7-icon v-else f7="checkmark_circle" class="margin-right-half"></f7-icon>
        {{ t('box.doneOrdering.cta') }}
      </f7-button>
      <p class="carton-done__help">{{ t('box.doneOrdering.ctaHelp') }}</p>
    </f7-block>

    <!-- CLOSED_WAITING — closed, a line still on its way. No ship button. -->
    <f7-block v-if="state === CartonReview.CLOSED_WAITING">
      <div class="carton-done__banner" role="status">
        <f7-icon f7="clock" class="carton-done__banner-icon"></f7-icon>
        <div>
          <div class="carton-done__banner-title">{{ t('box.doneOrdering.waiting.title') }}</div>
          <div class="carton-done__banner-body">
            {{ t('box.doneOrdering.waiting.body', { count: review.onItsWayCount }, review.onItsWayCount) }}
          </div>
        </div>
      </div>
    </f7-block>

    <!-- CLOSED_PREPARING — closed and settled, the review not sent yet. -->
    <f7-block v-if="state === CartonReview.CLOSED_PREPARING">
      <div class="carton-done__banner" role="status">
        <f7-icon f7="cube_box" class="carton-done__banner-icon"></f7-icon>
        <div>
          <div class="carton-done__banner-title">{{ t('box.doneOrdering.preparing.title') }}</div>
          <div class="carton-done__banner-body">{{ t('box.doneOrdering.preparing.body') }}</div>
        </div>
      </div>
    </f7-block>

    <!-- REVIEW — what the team checked, the media, three doors. Nothing else. -->
    <f7-block v-if="state === CartonReview.REVIEW">
      <div class="carton-done__review">
        <div class="carton-done__review-title">{{ t('box.doneOrdering.review.title') }}</div>
        <div class="carton-done__review-summary">{{ summary }}</div>

        <div v-if="review.media.length > 0" class="carton-done__media">
          <div class="carton-done__media-title">{{ t('box.doneOrdering.review.mediaTitle') }}</div>
          <div class="carton-done__gallery">
            <template v-for="(file, index) in review.media" :key="file.id">
              <video
                v-if="file.type === 'video'"
                class="carton-done__video"
                :src="file.url"
                :poster="file.thumbnailUrl || undefined"
                :aria-label="t('box.doneOrdering.review.videoLabel')"
                controls
                playsinline
                preload="metadata"
              />
              <button
                v-else
                type="button"
                class="carton-done__thumb"
                @click="openImage(file)"
              >
                <img
                  :src="file.thumbnailUrl || file.url"
                  :alt="t('box.doneOrdering.review.imageAlt', { n: index + 1 })"
                  loading="lazy"
                />
              </button>
            </template>
          </div>
        </div>

        <div v-if="review.complaint" class="carton-done__complaint" role="status">
          <div class="carton-done__complaint-sent">
            <f7-icon f7="checkmark_circle_fill" size="16"></f7-icon>
            {{ t('box.doneOrdering.review.complaintSent') }}
          </div>
          <div class="carton-done__complaint-label">
            {{ t('box.doneOrdering.review.complaintLabel') }}
          </div>
          <p class="carton-done__complaint-text">{{ review.complaint }}</p>
        </div>

        <f7-button
          fill
          large
          color="green"
          class="carton-done__accept"
          :disabled="shipping || isBusy"
          @click="emit('ship')"
        >
          <f7-preloader
            v-if="shipping"
            size="20"
            class="margin-right-half"
          ></f7-preloader>
          <f7-icon v-else f7="paperplane_fill" class="margin-right-half"></f7-icon>
          {{ t('box.doneOrdering.review.accept') }}
        </f7-button>
        <f7-button
          outline
          large
          class="carton-done__complain"
          :disabled="isBusy"
          @click="openComplaint"
        >
          {{ review.complaint ? t('box.doneOrdering.review.editComplaint') : t('box.doneOrdering.review.complain') }}
        </f7-button>
      </div>
    </f7-block>

    <!-- THE FAST LANE: today's ask-to-ship block, as the page draws it. Shown on
         an open carton, a carton the team is preparing, and wherever this
         component does not own the slot (no review state). -->
    <slot v-if="showFastLane" :fast-lane="fastLane" />

    <!-- The undo — hers any time before READY. A text link, never a button. -->
    <f7-block v-if="canResume" class="carton-done__resume">
      <f7-link
        :class="{ disabled: isBusy }"
        @click="confirmResume"
      >
        <f7-preloader
          v-if="isResumingOrdering"
          size="16"
          class="margin-right-half"
        ></f7-preloader>
        {{ t('box.doneOrdering.resume') }}
      </f7-link>
    </f7-block>

    <!-- THE COMPLAINT BOX — one textarea, 1–500 characters. Definite height with
         the action in the fixed footer (the sheet-scroll invariant). -->
    <f7-sheet
      v-if="state === CartonReview.REVIEW"
      class="carton-complaint-sheet"
      backdrop
      :opened="complaintOpen"
      @sheet:closed="complaintOpen = false"
    >
      <f7-toolbar>
        <div class="left">
          <f7-link sheet-close>{{ t('box.doneOrdering.complaint.cancel') }}</f7-link>
        </div>
        <div class="title">{{ t('box.doneOrdering.complaint.title') }}</div>
        <div class="right" />
      </f7-toolbar>

      <f7-page-content>
        <f7-block>
          <p class="carton-complaint__prompt">{{ t('box.doneOrdering.complaint.prompt') }}</p>
        </f7-block>
        <f7-list strong-ios outline-ios form>
          <f7-list-input
            type="textarea"
            class="carton-complaint__input"
            :value="draft"
            :maxlength="COMPLAINT_MAX"
            :placeholder="t('box.doneOrdering.complaint.placeholder')"
            @input="draft = $event.target.value"
          ></f7-list-input>
        </f7-list>
        <f7-block class="carton-complaint__counter">
          {{ t('box.doneOrdering.complaint.counter', { count: draft.length, max: COMPLAINT_MAX }) }}
        </f7-block>
      </f7-page-content>

      <template #fixed>
        <div class="sheet-fixed-footer">
          <f7-button
            fill
            large
            :disabled="!canSendComplaint"
            @click="sendComplaint"
          >
            <f7-preloader
              v-if="isSendingReviewComplaint"
              size="20"
              class="margin-right-half"
            ></f7-preloader>
            {{ t('box.doneOrdering.complaint.send') }}
          </f7-button>
        </div>
      </template>
    </f7-sheet>
  </div>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCart } from '@/composables/useCart.js';
import { CartonReview } from '@/utils/cartonReviewState.js';

const props = defineProps({
  /** The carton (its id is all this component reads off it). */
  box: { type: Object, required: true },
  /** `cartonReviewState(box)` — null when the slot is not this component's. */
  review: { type: Object, default: null },
  /** An ask-to-ship is in flight (the page's own flag) — the accept button waits. */
  shipping: { type: Boolean, default: false },
});

const emit = defineEmits(['ship']);

const COMPLAINT_MAX = 500;

const { t } = useI18n();
const {
  fetchBox,
  doneOrdering,
  doneOrderingError,
  isMarkingDoneOrdering,
  resumeOrdering,
  resumeOrderingError,
  isResumingOrdering,
  reviewComplaint,
  reviewComplaintError,
  isSendingReviewComplaint,
} = useCart();

const state = computed(() => props.review?.state ?? null);
const isBusy = computed(
  () => isMarkingDoneOrdering.value || isResumingOrdering.value || isSendingReviewComplaint.value,
);

// The fast lane (today's block) is visible whenever the team has not sent the
// review and nothing is still on its way: an open carton, or one being prepared.
// With no review state (Poola, anything this helper does not own) it is simply
// today's slot, untouched.
const showFastLane = computed(
  () => state.value === null
    || state.value === CartonReview.OPEN
    || state.value === CartonReview.CLOSED_PREPARING,
);
const fastLane = computed(
  () => state.value === CartonReview.OPEN || state.value === CartonReview.CLOSED_PREPARING,
);

// Open ordering again: from every closed state; the server clears the review and
// her complaint with it.
const canResume = computed(
  () => state.value === CartonReview.CLOSED_WAITING
    || state.value === CartonReview.CLOSED_PREPARING
    || state.value === CartonReview.REVIEW,
);

const summary = computed(() => {
  const missing = props.review?.missingCount ?? 0;
  return t(
    'box.doneOrdering.review.summary',
    { arrived: props.review?.arrivedCount ?? 0, missing, count: missing },
    missing,
  );
});

// ── TOASTS ──────────────────────────────────────────────────────────────────
const showSuccessToast = (message) => {
  f7.toast
    .create({ text: message, position: 'center', closeTimeout: 2000, cssClass: 'toast-success' })
    .open();
};

const showErrorToast = (message, closeTimeout = 4000) => {
  f7.toast
    .create({ text: message, position: 'center', closeTimeout, cssClass: 'toast-error' })
    .open();
};

// The carton is re-read after every verb — on success the stamps changed, on a 409
// this device's picture of it is the thing that was wrong.
const reread = async (boxId) => {
  try {
    await fetchBox(0, boxId, true);
  } catch (error) {
    console.error('Failed to re-read the carton:', error);
  }
};

// ── «خلصت طلباتي» ───────────────────────────────────────────────────────────
// `dialog.create` with explicit buttons, not `dialog.confirm`: the shorthand
// renders English OK/Cancel regardless of locale.
const confirmDone = () => {
  const boxId = props.box?.id;
  if (boxId == null || isBusy.value) return;

  f7.dialog
    .create({
      title: t('general.confirm'),
      text: t('box.doneOrdering.confirm'),
      buttons: [
        { text: t('general.cancel'), close: true },
        { text: t('general.confirm'), close: true, onClick: () => runDone(boxId) },
      ],
      verticalButtons: false,
    })
    .open();
};

const runDone = async (boxId) => {
  await doneOrdering(0, boxId);
  // A 409 is her sentence in her language, printed verbatim (`errorFromJsonApi`
  // already made it `.message`); the translated fallback is for a failure with no
  // sentence at all.
  if (doneOrderingError.value) {
    showErrorToast(doneOrderingError.value.message || t('box.feedback.errorDoneOrdering'));
    await reread(boxId);
    return;
  }
  showSuccessToast(t('box.doneOrdering.feedback.done'));
  await reread(boxId);
};

// ── THE WAY BACK ────────────────────────────────────────────────────────────
const confirmResume = () => {
  const boxId = props.box?.id;
  if (boxId == null || isBusy.value) return;

  f7.dialog
    .create({
      title: t('general.confirm'),
      text: t('box.doneOrdering.resumeConfirm'),
      buttons: [
        { text: t('general.cancel'), close: true },
        { text: t('general.confirm'), close: true, onClick: () => runResume(boxId) },
      ],
      verticalButtons: false,
    })
    .open();
};

const runResume = async (boxId) => {
  await resumeOrdering(0, boxId);
  if (resumeOrderingError.value) {
    showErrorToast(resumeOrderingError.value.message || t('box.feedback.errorResumingOrdering'));
    await reread(boxId);
    return;
  }
  showSuccessToast(t('box.doneOrdering.feedback.resumed'));
  await reread(boxId);
};

// ── THE COMPLAINT ───────────────────────────────────────────────────────────
const complaintOpen = ref(false);
const draft = ref('');

const canSendComplaint = computed(() => {
  const { length } = draft.value.trim();
  return length >= 1 && length <= COMPLAINT_MAX && !isSendingReviewComplaint.value;
});

// Opens prefilled with what she already wrote: editing, not starting over.
const openComplaint = () => {
  draft.value = props.review?.complaint ?? '';
  complaintOpen.value = true;
};

const sendComplaint = async () => {
  const boxId = props.box?.id;
  if (boxId == null || !canSendComplaint.value) return;

  await reviewComplaint(0, boxId, draft.value);
  if (reviewComplaintError.value) {
    // The sheet stays open on a failure: her words are still in the box. On a 409
    // the carton moved on (no review any more / no longer open) — the re-read
    // changes the slot, which closes the sheet with it.
    showErrorToast(reviewComplaintError.value.message || t('box.feedback.errorReviewComplaint'));
    await reread(boxId);
    return;
  }
  complaintOpen.value = false;
  showSuccessToast(t('box.doneOrdering.feedback.complaintSent'));
  await reread(boxId);
};

// ── THE MEDIA ───────────────────────────────────────────────────────────────
// Photos open in F7's photo browser (one place for the viewer config:
// useReceiptPhotoBrowser). A clip plays inline in the gallery itself.
let photoBrowser = null;

const closePhotoBrowser = () => {
  photoBrowser?.destroy?.();
  photoBrowser = null;
};

const openImage = (file) => {
  const images = (props.review?.media ?? []).filter((item) => item.type !== 'video');
  const index = Math.max(0, images.findIndex((item) => item.id === file.id));
  try {
    closePhotoBrowser();
    photoBrowser = f7.photoBrowser.create({
      photos: images.map((item) => ({ url: item.url })),
      type: 'popup',
      theme: 'dark',
      toolbar: false,
      navbar: true,
      swiper: { zoom: { enabled: true, maxRatio: 3, minRatio: 1 } },
      on: { close: closePhotoBrowser },
    });
    photoBrowser.open(index);
  } catch (error) {
    console.error('Failed to open the review photo:', error);
    window.open(file.url, '_blank', 'noopener,noreferrer');
  }
};

onBeforeUnmount(closePhotoBrowser);
</script>

<style scoped>
.carton-done__loading {
  text-align: center;
}

.carton-done__help {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  color: var(--poolit-text-secondary);
}

.carton-done__banner {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border-radius: 12px;
  background: var(--poolit-bg-secondary);
}

.carton-done__banner-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--poolit-text-secondary);
}

.carton-done__banner-title,
.carton-done__review-title {
  font-weight: 600;
  line-height: 1.4;
}

.carton-done__banner-body {
  margin-top: 2px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--poolit-text-secondary);
}

.carton-done__review {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--poolit-border-color);
  border-radius: 12px;
  background: var(--poolit-bg-primary);
}

.carton-done__review-title {
  font-size: 17px;
}

.carton-done__review-summary {
  font-size: 15px;
  line-height: 1.5;
}

.carton-done__media-title,
.carton-done__complaint-label {
  margin-bottom: 6px;
  font-size: 13px;
  color: var(--poolit-text-secondary);
}

.carton-done__gallery {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.carton-done__thumb {
  flex: 0 0 auto;
  width: 96px;
  height: 96px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  overflow: hidden;
  background: var(--poolit-bg-tertiary);
}

.carton-done__thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carton-done__video {
  flex: 0 0 auto;
  width: 220px;
  max-height: 160px;
  border-radius: 10px;
  background: #000;
}

.carton-done__complaint {
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--poolit-bg-secondary);
}

.carton-done__complaint-sent {
  display: flex;
  gap: 6px;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
}

.carton-done__complaint-label {
  margin-top: 8px;
  margin-bottom: 2px;
}

.carton-done__complaint-text {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.carton-done__resume {
  text-align: center;
}

/*
  DEFINITE height (a percentage of the app root, never vh/dvh) so the sheet's own
  scroll chain works — see tests/unit/styles/sheet-scroll-invariant.test.js. The
  action sits in the shared fixed footer declared in src/css/app.css.
*/
.carton-complaint-sheet {
  height: 70%;
  --sheet-fixed-footer-h: 76px;
  --f7-page-content-extra-padding-bottom: var(--sheet-fixed-footer-h);
}

.carton-complaint__prompt {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: var(--poolit-text-secondary);
}

.carton-complaint__counter {
  margin-top: 0;
  font-size: 12px;
  color: var(--poolit-text-secondary);
  text-align: end;
}
</style>
